// Step 2 of the client portal login.
//
// Reached from either login path - email + password, or the passwordless
// email-OTP login - whenever the account has a second factor enrolled.
//
// A client may hold several methods. The screen opens on their preferred one
// and offers "Use another method" to switch, the way Instagram and Facebook
// do: the usual path stays one tap, and the alternatives are there when a
// phone is lost or an app was wiped.
//
// The previous screen hands over a short-lived `challenge` through router
// state, never the URL. A direct visit has nothing to work with and bounces
// back to /login. A reload keeps the router state, so the login stays
// resumable until the challenge expires server-side (10 minutes).
//
// When SMS is the method offered first, the code was already sent by the
// login call itself.
import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';

import Auth from '../../components/layouts/auth';
import LoginAuth from '../../services/auth.service';
import ClientService from '../../services/client.service';
import { setClientId } from '../../feature/client.slice';
import { safeRedirect } from '../../utils/safeRedirect';

const METHOD_LABEL = {
  passkey: 'Use your passkey',
  sms: 'Text a code to your mobile',
  totp: 'Use your authenticator app',
};

const RESEND_COOLDOWN = 60;

const Login2fa = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [challenge, setChallenge] = useState(location.state?.challenge || null);
  const methods = location.state?.methods || [];
  const redirectTo = safeRedirect(location.state?.redirect) || '/dashboard';

  // Opens on the preferred method; "Use another method" swaps it.
  const [method, setMethod] = useState(
    location.state?.preferred || methods[0] || 'sms'
  );
  const phone = location.state?.phone || null;
  const [picking, setPicking] = useState(false);
  const others = methods.filter((m) => m !== method);

  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({ otp: '' });
  const [apiError, setApiError] = useState(false);
  const [apiMsg, setApiMsg] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [buttonClick, setButtonClick] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const isPasskey = method === 'passkey';
  const isSms = method === 'sms';

  useEffect(() => {
    if (!challenge) {
      navigate('/login', { replace: true });
    }
  }, [challenge, navigate]);

  useEffect(() => {
    // The login call sent the code, so the cooldown starts now rather than on
    // the first resend. Only on arrival - switchTo restarts it itself after
    // sending.
    if (method === 'sms') setSecondsLeft(RESEND_COOLDOWN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only an SMS can be re-sent, and only after a cooldown - an authenticator
  // code is derived on the device and a passkey has nothing to deliver.
  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setInterval(() => setSecondsLeft((v) => (v <= 1 ? 0 : v - 1)), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const formatCountdown = (v) =>
    `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`;

  // Switching method mid-login. SMS needs a code sent for the new method;
  // the authenticator and passkey have nothing to deliver.
  const switchTo = async (m) => {
    setPicking(false);
    setMethod(m);
    setOtp('');
    setErrors({ otp: '' });
    setAttemptsLeft(null);

    if (m === 'sms') {
      try {
        await LoginAuth.startTwoFactor({ challenge, method: 'sms' });
        setSecondsLeft(RESEND_COOLDOWN);
        flash('A code has been sent to your mobile.');
      } catch (err) {
        flash(err.response?.data?.error || 'Could not send the code');
      }
    }
  };

  const handleResend = async () => {
    if (secondsLeft > 0) return;
    setResending(true);
    try {
      await LoginAuth.resendTwoFactor({ challenge });
      // Wrong guesses carry over to the new code server-side; the count shows
      // again with the next wrong one.
      setAttemptsLeft(null);
      setSecondsLeft(RESEND_COOLDOWN);
      flash('A new code has been sent to your mobile.');
    } catch (err) {
      flash(err.response?.data?.error || 'Could not resend the code');
    } finally {
      setResending(false);
    }
  };

  // The Auth layout only renders its alert while `buttonClick` is true, so the
  // flag stays set for the life of the message and is cleared with it.
  const flash = (text) => {
    setButtonClick(true);
    setApiError(true);
    setApiMsg(text);
    setTimeout(() => {
      setButtonClick(false);
      setApiError(false);
      setApiMsg('');
    }, 4000);
  };

  const fetchClientAndStore = async (userId) => {
    try {
      const { clientIds } = await ClientService.getclient({ userId });
      localStorage.setItem('client', JSON.stringify(clientIds));
      dispatch(setClientId(clientIds[0].id));
    } catch (e) {
      console.error(e);
    }
  };

  // Passkey: the device signs a server-issued challenge behind a
  // fingerprint / face / PIN prompt. Nothing is typed and nothing reusable
  // crosses the wire.
  const handlePasskey = async () => {
    if (!browserSupportsWebAuthn()) {
      flash('This browser does not support passkeys.');
      return;
    }
    setVerifying(true);
    try {
      const options = await LoginAuth.passkeyLoginBegin({ challenge });
      const assertion = await startAuthentication({ optionsJSON: options });
      const res = await LoginAuth.passkeyLoginVerify({ challenge, response: assertion });

      await fetchClientAndStore(res.data.user.user.id);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        flash('Passkey sign-in was cancelled.');
      } else {
        flash(err.response?.data?.error || err.message || 'Passkey sign-in failed');
      }
      setVerifying(false);
    }
  };

  // Offer the prompt immediately - the browser needs the click to have come
  // from the user, so this only pre-warms; the button below does the work.
  useEffect(() => {
    if (challenge && isPasskey) setOtp('');
  }, [challenge, isPasskey]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!/^\d{6}$/.test(otp)) {
      setErrors({ otp: 'Code must be 6 digits' });
      return;
    }
    setErrors({ otp: '' });
    setVerifying(true);

    try {
      const res = await LoginAuth.verifyTwoFactor({ challenge, otp, method });
      await fetchClientAndStore(res.data.user.user.id);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const data = err.response?.data || {};
      setAttemptsLeft(typeof data.attemptsLeft === 'number' ? data.attemptsLeft : null);
      flash(data.error || 'Verification failed');

      // Allowance spent: the challenge is dead server-side, so there is
      // nothing left to submit. Back to the password screen.
      if (data.attemptsLeft === 0) {
        setChallenge(null);
      }
      setVerifying(false);
    }
  };

  if (!challenge) return null; // redirecting to /login

  return (
    <Auth
      pageTitle="Two-Factor Authentication"
      buttonClick={buttonClick}
      ApiError={apiError}
      ApiErrorMessage={apiMsg}
    >
      {picking ? (
        <>
          <p className="text-center text-muted mb-4" style={{ fontSize: '0.85rem' }}>
            Choose how you'd like to verify it's you.
          </p>
          <div className="d-grid gap-2">
            {methods.map((m) => (
              <button
                key={m}
                type="button"
                className="method-option"
                onClick={() => switchTo(m)}
              >
                <span className="method-option-title">{METHOD_LABEL[m] || m}</span>
                {m === method && (
                  <span className="method-option-blurb">Currently selected</span>
                )}
              </button>
            ))}
          </div>
          <Button variant="link" className="p-0 mt-3 d-block mx-auto" onClick={() => setPicking(false)}>
            Back
          </Button>
          <Link to="/login" className="secondary-cta">Back to Login</Link>
        </>
      ) : isPasskey ? (
        <>
          <p className="text-center text-muted mb-4" style={{ fontSize: '0.85rem' }}>
            Use your fingerprint, face or device PIN to finish signing in.
          </p>
          <div className="d-flex justify-content-center">
            <Button
              className="btn btn-md px-4 rounded"
              type="button"
              variant="btn btn-primary"
              onClick={handlePasskey}
              disabled={verifying}
            >
              {verifying ? 'Waiting for your device…' : 'Use passkey'}
            </Button>
          </div>
          {others.length > 0 && (
            <div className="countdown-text">
              <button
                type="button"
                className="otp-resend-link btn btn-link p-0"
                onClick={() => setPicking(true)}
              >
                Use another method
              </button>
            </div>
          )}
          <Link to="/login" className="secondary-cta">Back to Login</Link>
        </>
      ) : (
      <Form onSubmit={handleVerify}>
        <p className="text-center text-muted mb-4" style={{ fontSize: '0.85rem' }}>
          {isSms ? (
            <>A 6-digit code has been sent to {phone || 'your mobile'}. Enter it below.</>
          ) : (
            <>Open your authenticator app and enter the 6-digit code for <strong>ADM One</strong>.</>
          )}
        </p>

        <Form.Group className="form-group">
          <div className="text-center">
            <Form.Label htmlFor="otp">{isSms ? 'OTP' : 'Authenticator code'}</Form.Label>
          </div>
          <div className="otp-input-container">
            <Form.Control
              type="text"
              id="otp"
              inputMode="numeric"
              autoComplete={isSms ? 'one-time-code' : 'off'}
              autoFocus
              maxLength={6}
              placeholder=" "
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              isInvalid={!!errors.otp}
            />
          </div>
          {errors.otp && (
            <div className="text-danger text-center mt-2" style={{ fontSize: '0.85rem' }}>
              {errors.otp}
            </div>
          )}
        </Form.Group>

        {attemptsLeft !== null && attemptsLeft > 0 && (
          <p className="countdown-text text-danger mb-0" style={{ fontSize: '0.85rem' }}>
            {attemptsLeft} {attemptsLeft === 1 ? 'attempt' : 'attempts'} remaining
          </p>
        )}

        <div className="d-flex justify-content-center pt-3">
          <Button
            className="btn btn-md px-4 rounded"
            type="submit"
            variant="btn btn-primary"
            disabled={verifying}
          >
            {verifying ? 'Verifying…' : 'Verify'}
          </Button>
        </div>

        {isSms ? (
          <div className="countdown-text">
            {secondsLeft > 0 ? (
              <span className="otp-timer text-muted d-block">
                Resend code in {formatCountdown(secondsLeft)}
              </span>
            ) : (
              <button
                type="button"
                className="otp-resend-link btn btn-link p-0"
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? 'Sending…' : 'Resend code'}
              </button>
            )}
          </div>
        ) : (
          /* Nothing to resend: an authenticator code is derived on the device,
             not delivered. It simply rolls over every 30 seconds. */
          <p className="countdown-text text-muted mb-0" style={{ fontSize: '0.8rem' }}>
            The code changes every 30 seconds.
          </p>
        )}

        {others.length > 0 && (
          <div className="countdown-text">
            <button
              type="button"
              className="otp-resend-link btn btn-link p-0"
              onClick={() => setPicking(true)}
            >
              Use another method
            </button>
          </div>
        )}

        <Link to="/login" className="secondary-cta">Back to Login</Link>
      </Form>
      )}
    </Auth>
  );
};

export default Login2fa;
